import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { canSee } from "../../utils/sectionPermissions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { ReactComponent as NoAccess } from "../../img/svg/NoAccess.svg";
import DynamicTable from "../../store/DynamicTable";
import styles from "./AllUsers.module.css";

const AllUsers = (props) => {
  const navigate = useNavigate();
  const [trigger, setTrigger] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allUserData, setAllUserData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [trigger]);

  const fetchUsers = () => {
    fetch(`${process.env.REACT_APP_SERVER_URL}/all_users`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response) => {
        if (response.status === 200) return response.json();
        if (response.status === 202) {
          navigate("/login", { state: { sessionExpired: true } });
        } else {
          console.log("Error fetching users");
        }
      })
      .then((data) => {
        if (data?.data) {
          const sortedUsers = [...data.data].sort((a, b) => {
            const aTime = new Date(a?.createdAt || 0).getTime();
            const bTime = new Date(b?.createdAt || 0).getTime();
            return bTime - aTime;
          });
          setAllUserData(sortedUsers);
        }
      });
  };

  const stats = useMemo(() => {
    const total = allUserData.length;
    const verified = allUserData.filter((u) => u?.email_verified).length;
    const withCompany = allUserData.filter((u) =>
      String(u?.company || "").trim(),
    ).length;
    const withPlan = allUserData.filter((u) =>
      String(u?.plan || "").trim(),
    ).length;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const newLast30 = allUserData.filter((u) => {
      const t = new Date(u?.createdAt || 0).getTime();
      return t >= thirtyDaysAgo;
    }).length;
    return { total, verified, withCompany, withPlan, newLast30 };
  }, [allUserData]);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allUserData;
    return allUserData.filter((u) => {
      const hay = [
        u?.firstName,
        u?.lastName,
        u?.email,
        u?.company,
        u?.plan,
        u?.id != null ? String(u.id) : "",
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [allUserData, searchTerm]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/register_user`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData),
        }
      );
      const data = await res.json();
      console.log(data);

      // The handler answers 201 with the new user in `data`; the admin auth
      // middleware also uses 201 (with data: "") for "please sign in".
      if (res.status === 201 && data?.data) {
        setShowModal(false);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          password: "",
        });
        fetchUsers();
        showSnackbar("User registered successfully!", "success");
      } else {
        showSnackbar(data?.msg || "Failed to register user", "error");
      }
    } catch (error) {
      console.log("error", error);
      showSnackbar("Failed to register user", "error");
    }
  };

  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  const handleVerifyUser = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/verify_user`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ id: userId }),
        }
      );

      if (res.status === 200) {
        showSnackbar("User verified successfully!", "success");
        setTrigger((prev) => !prev);
      } else {
        showSnackbar("Verification failed", "error");
      }
    } catch (error) {
      console.error("Error verifying user:", error);
      showSnackbar("Error verifying user", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const generateUserLoginLink = async (userId) => {
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/generate_login_token`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ userId }),
      }
    );

    const data = await res.json();
    if (!data.success || !data.loginUrl) {
      throw new Error("Failed to generate login link");
    }
    return data.loginUrl;
  };

  const copyToClipboard = async (value) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const handleLoginAsUser = async (user) => {
    const confirmLogin = window.confirm(
      `Are you sure you want to login as ${user.firstName} ${user.lastName}?`
    );
    if (!confirmLogin) return;

    try {
      const loginUrl = await generateUserLoginLink(user.id);
      window.open(loginUrl, "_blank");
    } catch (error) {
      console.error("Error logging in as user:", error);
      showSnackbar("Login failed", "error");
    }
  };

  const handleCopyUserLink = async (user) => {
    try {
      const loginUrl = await generateUserLoginLink(user.id);
      await copyToClipboard(loginUrl);
      showSnackbar("Login link copied successfully!", "success");
    } catch (error) {
      console.error("Error copying login link:", error);
      showSnackbar("Failed to copy login link", "error");
    }
  };

  return (
    <>
      {canSee(props.userRole, "allusers") && (
        <>
          <div className={styles.container}>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {[
                { label: "Total users", value: stats.total, color: "#134f8b" },
                { label: "Verified", value: stats.verified, color: "#1f8a3a" },
                { label: "With company", value: stats.withCompany, color: "#225378" },
                { label: "On a plan", value: stats.withPlan, color: "#7953ff" },
                { label: "New · 30d", value: stats.newLast30, color: "#c0392b" },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    flex: "1 1 140px",
                    minWidth: 140,
                    background: "#fff",
                    borderRadius: 12,
                    padding: "12px 16px",
                    border: "1px solid #e6eef6",
                    boxShadow: "0 1px 3px rgba(15,27,44,0.04)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: "#8696aa",
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: s.color,
                      marginTop: 2,
                    }}
                  >
                    {s.value.toLocaleString("en-IN")}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                marginBottom: 14,
                flexWrap: "wrap",
              }}
            >
              <input
                type="text"
                placeholder="Search by name, email, company, plan, id…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: "1 1 320px",
                  minWidth: 240,
                  padding: "10px 14px",
                  border: "1px solid #d8e2ec",
                  borderRadius: 999,
                  fontSize: 14,
                  outline: "none",
                  background: "#fff",
                }}
              />
              {searchTerm && (
                <span style={{ color: "#607084", fontSize: 13 }}>
                  Showing <strong>{filteredUsers.length.toLocaleString("en-IN")}</strong> of{" "}
                  {stats.total.toLocaleString("en-IN")}
                </span>
              )}
              <button
                className={styles.addUserBtn}
                onClick={() => setShowModal(true)}
              >
                + Add User
              </button>
            </div>

            <DynamicTable
              data={filteredUsers}
              columns={[
                "id",
                "firstName",
                "lastName",
                "email",
                "plan",
                "company",
                "email_verified",
                "Open User",
              ]}
              onVerify={handleVerifyUser}
              handleLoginAsUser={handleLoginAsUser}
              handleCopyUserLink={handleCopyUserLink}
            />

            {showModal && (
              <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                  <h2 className={styles.modalTitle}>Register New User</h2>
                  <form
                    onSubmit={handleFormSubmit}
                    className={styles.modalForm}
                  >
                    {["firstName", "lastName", "email", "phone"].map(
                      (field) => (
                        <input
                          key={field}
                          type="text"
                          name={field}
                          placeholder={
                            field.charAt(0).toUpperCase() + field.slice(1)
                          }
                          value={formData[field]}
                          onChange={handleInputChange}
                          className={styles.inputField}
                          required
                        />
                      )
                    )}

                    <div className={styles.passwordContainer}>
                      <input
                        type="text"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={styles.inputField}
                        required
                      />
                      <button
                        type="button"
                        className={styles.generateBtn}
                        onClick={() => {
                          const newPassword = generatePassword();
                          setFormData((prev) => ({
                            ...prev,
                            password: newPassword,
                          }));
                        }}
                      >
                        Generate
                      </button>
                    </div>
                    <div className={styles.modalButtons}>
                      <button type="submit" className={styles.submitBtn}>
                        Submit
                      </button>
                      <button
                        type="button"
                        className={styles.cancelBtn}
                        onClick={() => {
                          setShowModal(false);
                          setFormData({
                            userName: "",
                            firstName: "",
                            lastName: "",
                            email: "",
                            phone: "",
                            password: "",
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </div>
        </>
      )}
      {!canSee(props.userRole, "allusers") && (
          <Box>
            <div>
              <center>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <NoAccess style={{ height: "50vh" }}></NoAccess>
                </Box>
              </center>
            </div>
          </Box>
        )}
    </>
  );
};

export default AllUsers;
